import router from '@/router'
import { Vue } from 'vue/types/vue'
import { ObjectUtils } from '@/commons/utils/objects'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { ApplicationUtils } from '@/commons/utils/application'
import { TranslationUtils } from '@/commons/utils/translations'

import {
    ErrorActivity,
    RequestClientErrorStatus,
    RequestServerErrorStatus
} from '@/commons/constants/error'
import { AuthenticationService, errorStore } from '@/store'

import {
    ErrorPayload,
    ValidationError,
    ValidationObserver,
    NormalizedRequestErrors
} from '@/commons/types'

import {
    BaseError,
    GeneralError,
    RequestServerError,
    RequestGeneralError,
    RequestValidationError,
    RequestAuthenticationError
} from '@/store/entities'
import {
    validatorIds,
    ContactValidationFieldName,
    BiometryValidationFieldName,
    ApplicantValidationFieldName,
    ApplicationValidationFieldName,
    IdCardValidationFieldName,
    CitizenPassportValidationFieldName,
    ErrorMessageKeys,
    Urls
} from '@/commons/constants/application'

type STOP_PROPAGATION = false | void

const REDIRECT_ACTIVITIES = [
    ErrorActivity.REDIRECT,
    ErrorActivity.LOGOUT
]

const AUTHENTICATION_ERRORS = [
    RequestClientErrorStatus.UNAUTHORIZED,
    RequestClientErrorStatus.FORBIDDEN
]

const TRANSLATIONS_WITH_URL = [
    ErrorMessageKeys.FINGERPRINTS_NOT_FOUND.toString(),
    ErrorMessageKeys.PERSON_INVALID_CITIZENSHIP.toString(),
    ErrorMessageKeys.PERSON_INVALID_EU_CITIZENSHIP.toString(),
    ErrorMessageKeys.PERSON_ALIVE_UNDEFINED.toString()
]

const TRANSLATIONS_WITH_URL_AT_END = [
    ErrorMessageKeys.PERSON_INVALID_CITIZENSHIP.toString(),
    ErrorMessageKeys.PERSON_ALIVE_UNDEFINED.toString()
]

const TRANSLATIONS_WITH_URL_IN_MIDDLE = [
    ErrorMessageKeys.FINGERPRINTS_NOT_FOUND.toString()
]

export const FORM_VALIDATION_FIELDS: Array<string> = [
    ...Object.values(ApplicationValidationFieldName),
    ...Object.values(BiometryValidationFieldName),
    ...Object.values(IdCardValidationFieldName),
    ...Object.values(CitizenPassportValidationFieldName),
    ...Object.values(ApplicantValidationFieldName),
    ...Object.values(ContactValidationFieldName)
]

const SERVER_ERRORS = Object.values(RequestServerErrorStatus)

function isBaseError (err: unknown): boolean {
    return typeof err === 'object' && err !== null &&
        Object.prototype.hasOwnProperty.call(err, '_messageTranslationKeys')
}

function isRequestError (err: unknown): boolean {
    return typeof err === 'object' && err !== null && err instanceof AxiosError
}

function resolveBaseErrorMessage (err: BaseError): ErrorPayload {
    return {
        errorTitleTranslationKey: err.titleTranslationKey,
        messageTranslationKeys: err.messageTranslationKeys,
        errorCode: err.errorId
    }
}

function isRedirect (errorPath: string, currentActivity: number): boolean {
    return router.currentRoute.path !== errorPath && REDIRECT_ACTIVITIES.includes(currentActivity)
}

async function resolveErrorActivity (error: BaseError): Promise<STOP_PROPAGATION> {
    const activity = error.errorActivity

    if (activity === ErrorActivity.LOGOUT) {
        await AuthenticationService.logout().catch(() => undefined)
    }

    // No need to redirect when redirect page is same as current page
    if (isRedirect(error.errorPath, activity)) {
        await router.push(error.errorPath).catch(() => undefined)
    }

    // Blocking full redirect of cancelled payment is partially necessary, otherwise error modal is not shown to user
    if (error.messageTranslationKeys[0] === 'errors.payment.message') {
        await router.push(error.errorPath).catch(() => undefined)
    }

    const errorPayload = resolveBaseErrorMessage(error)
    errorStore.setError(errorPayload)
    return false
}

export class ErrorUtils {
    static requestErrorHandler (error: unknown): void {
        ErrorUtils.globalErrorHandler(error).then().catch(() => {
            const errorPayload = resolveBaseErrorMessage(new RequestGeneralError())
            errorStore.setError(errorPayload)
        })
    }

    static routerErrorHandler (error: unknown): void {
        ErrorUtils.globalErrorHandler(error).then().catch(() => {
            const errorPayload = resolveBaseErrorMessage(new GeneralError())
            errorStore.setError(errorPayload)
        })
    }

    static async globalErrorHandler (error: unknown): Promise<STOP_PROPAGATION> {
        // consecutive errors in async chain have error as "undefined"
        if (error === undefined) {
            return false
        }

        if (axios.isCancel(error)) {
            return false
        }

        let definedError = new GeneralError()
        if (isBaseError(error)) {
            definedError = error as BaseError
        }

        return await resolveErrorActivity(definedError)
    }

    // Convenience function for handling saving
    static handleSavingError (error: unknown, vm: ValidationObserver, titleKey?: string): void {
        const errorInstance = ErrorUtils.resolveRequestErrorEntity(error, titleKey)

        // Is not a validation type error (i.e unauthorized or internal server error)
        if (typeof errorInstance === 'object' && errorInstance !== null &&
            !Object.prototype.hasOwnProperty.call(errorInstance, '_errorFields')) {
            ErrorUtils.requestErrorHandler(errorInstance)
            return
        }

        // Error fields are present but not all of them are on form validation.
        // Hence we should handle it as general request error with validation message translations
        const validationFields = (errorInstance as RequestValidationError).errorFields
        if (validationFields.length !== (errorInstance as RequestValidationError).messageTranslationKeys.length ||
            !validationFields.every(fieldName => ErrorUtils.isFormValidationField(fieldName))) {
            const requestValidationEntity = new RequestValidationError([])
            ErrorUtils.requestErrorHandler(new RequestGeneralError(
                requestValidationEntity.messageTranslationKeys,
                requestValidationEntity.titleTranslationKey
            ))
            return
        }

        let errorMessageIndex = -1
        validationFields.forEach((fieldName: string, index: number) => {
            const errorCode = (errorInstance as BaseError).messageTranslationKeys[index]
            ApplicationUtils.handleBackendError(fieldName, errorCode, vm)
            if (fieldName === ApplicationValidationFieldName.DISABILITY_CONFIRMATION) {
                errorMessageIndex = index
            }
        })

        if (errorMessageIndex >= 0 && isBaseError(errorInstance)) {
            const disabilityError = (errorInstance as BaseError)
            disabilityError.messageTranslationKeys = [
                disabilityError.messageTranslationKeys[errorMessageIndex]
            ]
            ErrorUtils.requestErrorHandler(disabilityError)
        }
    }

    // Wrapper function for determining if error should be handled as system or axios error
    static resolveRequestErrorEntity (error: unknown, titleKey?: string): BaseError | unknown {
        if (axios.isCancel(error) || isBaseError(error)) {
            return error
        }

        const response = ErrorUtils.getRequestErrorResponse(error)
        if (!response) {
            return new RequestGeneralError()
        }
        return ErrorUtils.getRequestErrorEntity(response, titleKey)
    }

    // Resolve is valid request error and return request response entity or undefined
    static getRequestErrorResponse (error: unknown): AxiosResponse | undefined {
        if (!isRequestError(error)) {
            return undefined
        }
        const response: AxiosResponse | undefined = (error as AxiosError).response
        if (!response || typeof response.data !== 'object' || ObjectUtils.isEmpty(response.data)) {
            return undefined
        }
        return response
    }

    // We should resolve request error
    static getRequestErrorEntity (response: AxiosResponse | undefined, titleKey?: string): BaseError {
        let entity = new RequestGeneralError()

        if (!response) {
            return entity
        }
        entity.errorId = response.data.errorId || ''

        // Trying to get normalized errors
        const normalizedErrors = (response.data as NormalizedRequestErrors).errors
        const errorStatus = response.status

        // check that error list contains error objects and all error objects has "code" key with "truthy" content
        if (!normalizedErrors || normalizedErrors.length < 1 || normalizedErrors.some(e => !e.code)) {
            return entity
        }

        if (AUTHENTICATION_ERRORS.indexOf(errorStatus) >= 0) {
            return new RequestAuthenticationError()
        } else if (SERVER_ERRORS.indexOf(errorStatus) >= 0 && normalizedErrors.some(e => !e.code)) {
            return new RequestServerError()
        } else if (normalizedErrors.every(e => e.field) && response.status === RequestClientErrorStatus.BAD_REQUEST) {
            const errorFields = normalizedErrors.map(e => e.field) as Array<string>
            entity = new RequestValidationError(errorFields)
        }

        try {
            entity.messageTranslationKeys = normalizedErrors.map(
                e => TranslationUtils.mapRequestErrorTranslationKey([
                    e.field ? e.field : e.code,
                    e.code
                ], entity.translationGroup)
            )
        } catch {
            // Fail silently
        }

        if (titleKey) {
            entity.titleTranslationKey = titleKey
        }
        return entity
    }

    static hasValidationErrors (
        vm: ValidationObserver, errors: Record<string, Array<ValidationError | string>>
    ): boolean {
        return !ObjectUtils.isEmpty(errors) && Object.values(errors).some(e => e.length > 0)
    }

    static isFormValidationField (fieldName: string): boolean {
        return FORM_VALIDATION_FIELDS.indexOf(fieldName) >= 0
    }

    static scrollToFirstError (vm: Vue, errors: Record<string, Array<ValidationError | string>>): void {
        for (const key in errors) {
            if (!Object.prototype.hasOwnProperty.call(errors, key) ||
                !Object.prototype.hasOwnProperty.call(validatorIds, key)) {
                continue
            }

            const element = vm.$el.querySelector(`#${validatorIds[key]}`)
            if (errors[key].length > 0 && element) {
                this.scrollToErrorElement(element)
                break
            }
        }
    }

    static scrollToErrorElement (element: Element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        })
    }

    static isUrlInErrorMessage (key: string): boolean {
        return TRANSLATIONS_WITH_URL.includes(key)
    }

    static isUrlAtEndOfErrorMessage (key: string): boolean {
        return TRANSLATIONS_WITH_URL_AT_END.includes(key)
    }

    static isUrlInMiddleOfErrorMessage (key: string): boolean {
        return TRANSLATIONS_WITH_URL_IN_MIDDLE.includes(key)
    }

    static getErrorMessageUrl (key: string): string {
        switch (key) {
        case ErrorMessageKeys.FINGERPRINTS_NOT_FOUND:
            return Urls.PPA_DEPARTMENTS
        case ErrorMessageKeys.PERSON_INVALID_CITIZENSHIP:
            return Urls.PPA_FOREIGNER_SELF_SERVICE
        case ErrorMessageKeys.PERSON_ALIVE_UNDEFINED:
            return Urls.PPA_E_RESIDENT
        default:
            return ''
        }
    }
}
